/**
 * /partner-bmw — Waste Management (BMW) Partners landing + registration.
 * Images are CMS-managed via /admin/content/page-images (page "partner-bmw").
 */
import React from "react";
import {
  ArrowRight, Biohazard, Truck, QrCode, CheckCircle, FileText, Link as LinkIcon,
  Flask, Buildings, UsersThree, CalendarBlank, MapPin, ClipboardText,
  CreditCard, ChartLineUp, Headset, ShieldCheck, Gear, SealCheck, Handshake,
  SquaresFour, Path, UsersFour, ChartBar, Wallet, Files, GearSix, Hospital,
} from "@phosphor-icons/react";
import { MarketingNav, MarketingFooter } from "./_shared";
import { PartnerRegisterForm } from "./PartnerRegisterForm";
import { usePageImages } from "../../lib/pageImages";
import SEO, { CITY_BRANCHES, organizationSchema, localBusinessSchema } from "../../lib/seo";

const RED = "#D23247";
const GEN = "https://static.prod-images.emergentagent.com/jobs/de359dc7-b870-46d7-8cc8-eee50ec58aa9/images/";
const HERO_DEFAULT = GEN + "66e6e9d33b8ba6b121b82ba9d67814ff716b6fd87e38c45c41ea771929f2be50.jpeg";

const WORKFLOW = [
  { Icon: Biohazard,   label: "New Collection Request", sub: "From Clinic · Kazhakkoottam" },
  { Icon: Truck,       label: "Route Assignment",       sub: "3 locations scheduled" },
  { Icon: QrCode,      label: "QR Bag Scan",            sub: "Scan to record collection" },
  { Icon: CheckCircle, label: "Collection Recorded",    sub: "12.4 kg · Biomedical waste", ok: true },
  { Icon: FileText,    label: "Disposal Documentation", sub: "Generate transfer record" },
];

const OPPORTUNITIES = [
  { key: "biomedical", Icon: LinkIcon, title: "Biomedical Waste Collection",
    body: "Explore collection opportunities for eligible healthcare facilities and other authorised sources.",
    photo: GEN + "e15d24ca1e6257c50a1681eda30dc585d5df42db7cf40c72c9beae803a77da76.jpeg" },
  { key: "sanitary", Icon: Flask, title: "Sanitary & Hygiene Waste Services",
    body: "Coordinate eligible sanitary waste and absorbent hygiene waste collection.",
    photo: GEN + "625e152de9aeda5fa6321a786c5e4af532ea1aa4ee327a07380ebaa1951c2859.jpeg" },
  { key: "institutional", Icon: Buildings, title: "Institutional Waste Management",
    body: "Connect with hospitals, clinics, offices and institutions seeking waste management services.",
    photo: GEN + "9bf740aeb1bab3405ff16d8e9c47e57886c883773606f7d44f202adbe4efac17.jpeg" },
  { key: "logistics", Icon: Truck, title: "Collection & Logistics Partnerships",
    body: "Participate in eligible collection, transport and service coordination activities.",
    photo: GEN + "a5f3d629a4cc045c57117044ff0c1108ed05fb13b66337428e6f4a72bf5284dd.jpeg" },
];

const BENEFITS = [
  { Icon: UsersThree,    title: "Customer & Contract Discovery", body: "Receive eligible service enquiries in your operating area." },
  { Icon: CalendarBlank, title: "Digital Collection Scheduling", body: "Manage pickup requests, assignments and rescheduling." },
  { Icon: MapPin,        title: "Route & Fleet Coordination",    body: "Organise collection routes and vehicle assignments." },
  { Icon: QrCode,        title: "QR-Enabled Waste Tracking",     body: "Use supported bag identification and collection-record workflows." },
  { Icon: ClipboardText, title: "Compliance Documentation",      body: "Maintain applicable collection and transfer records." },
  { Icon: CreditCard,    title: "Payments & Settlements",        body: "Review invoices, agreed charges and settlement information." },
  { Icon: ChartLineUp,   title: "Partner Dashboard",             body: "Access requests, team activities and operational reports." },
  { Icon: Headset,       title: "Partner Support",               body: "Get onboarding guidance, account assistance and platform updates." },
];

const PROCESS = [
  { Icon: FileText,    title: "Register Your Company",       body: "Provide business information, operating locations and service categories." },
  { Icon: ShieldCheck, title: "Complete Verification",       body: "Submit relevant authorisations, vehicle documentation and other applicable records." },
  { Icon: Gear,        title: "Configure Your Operations",   body: "Set service territories, vehicles, operating hours and collection capabilities." },
  { Icon: Files,       title: "Review Partnership Terms",    body: "Understand fees, responsibilities and operational requirements." },
  { Icon: SealCheck,   title: "Get Activated",               body: "Access supported collection requests and digital coordination tools." },
];

const Eyebrow = ({ children }) => (
  <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-slate-500 font-semibold">
    <span className="w-8 h-[2px] bg-[#D23247]"/>{children}
  </div>
);

const IconTile = ({ Icon, size = 18, className = "w-10 h-10" }) => (
  <div className={`${className} rounded-xl bg-[#FDE2E4] text-[#D23247] grid place-items-center shrink-0`}>
    <Icon size={size} weight="duotone"/>
  </div>
);

const RegisterForm = ({ compact }) => (
  <PartnerRegisterForm compact={compact} category="biomedical_waste" tag="BMW partner registration"
    testPrefix="bmw" title="Register Your Waste Company"
    subtitle="Join the Rescigo Health waste management network."
    defaultMessage="I'd like to register as a waste management partner."
    successMsg="Thanks! Our waste partnerships team will reach out."/>
);

function WorkflowCard({ c, i }) {
  return (
    <div data-testid={`bmw-workflow-${i}`}
         className="bmw-rise bg-white/95 backdrop-blur-md rounded-xl shadow-lg border border-[#F0EEEA] p-2.5 pr-4 flex items-center gap-3 w-[230px]"
         style={{ animationDelay: `${150 + i * 90}ms` }}>
      <div className={`w-9 h-9 rounded-lg grid place-items-center shrink-0 ${c.ok ? "bg-[#DCFCE7] text-[#15803D]" : "bg-[#FDE2E4] text-[#D23247]"}`}>
        <c.Icon size={18} weight={c.ok ? "fill" : "duotone"}/>
      </div>
      <div className="min-w-0">
        <div className="text-[12px] font-semibold text-slate-900 leading-tight">{c.label}</div>
        <div className="text-[10px] text-slate-500 leading-tight mt-0.5">{c.sub}</div>
      </div>
    </div>
  );
}

function Hero({ img }) {
  return (
    <section className="pt-24 sm:pt-28 pb-10 sm:pb-14 lg:pb-36 max-w-7xl mx-auto px-5 lg:px-10">
      <div className="grid lg:grid-cols-[1fr_1.15fr] gap-10 lg:gap-12 items-start">
        <div className="bmw-rise">
          <Eyebrow>Partner Network</Eyebrow>
          <h1 data-testid="bmw-hero-title"
              className="font-heading text-[34px] sm:text-5xl lg:text-[54px] leading-[1.05] mt-5 text-slate-900 tracking-tight">
            Waste Management Partners
            <span className="block text-[#D23247] mt-1">Grow your collection network. Simplify your operations.</span>
          </h1>
          <p className="text-base sm:text-lg text-slate-600 mt-5 max-w-lg leading-relaxed">
            Connect your waste management business with service opportunities, digital collection
            workflows and operational coordination through the Rescigo Health partner network.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <a href="#bmw-register" data-testid="bmw-hero-register"
               className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#D23247] hover:bg-[#B52236] text-white text-sm font-semibold transition-colors">
              Register Your Company <ArrowRight size={14} weight="bold"/>
            </a>
            <a href="#bmw-opportunities" data-testid="bmw-hero-explore"
               className="inline-flex items-center gap-2 h-12 px-6 rounded-full border border-[#D23247] text-[#D23247] hover:bg-[#FDE2E4] text-sm font-semibold transition-colors">
              Explore Waste Solutions <ArrowRight size={14} weight="bold"/>
            </a>
          </div>
          <div className="mt-8 grid grid-cols-2 gap-2 md:hidden">
            {WORKFLOW.slice(0, 4).map((c, i) => (
              <div key={i} className="flex items-center gap-2 bg-white border border-[#F0EEEA] rounded-lg p-2">
                <IconTile Icon={c.Icon} size={14} className="w-7 h-7"/>
                <span className="text-[11px] font-semibold text-slate-800 leading-tight">{c.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative">
          <div className="relative rounded-[28px] overflow-hidden aspect-[4/3] lg:aspect-[4/5] bg-[#FDE2E4]">
            <img src={img} alt="Rescigo biomedical waste collection team loading a waste truck"
                 data-testid="bmw-hero-image" className="absolute inset-0 w-full h-full object-cover object-[55%_30%]"/>
            <div className="absolute inset-0 bg-gradient-to-r from-black/25 via-transparent to-transparent"/>
          </div>
          <div className="hidden md:flex lg:hidden xl:flex flex-col gap-2.5 absolute top-5 left-5">
            {WORKFLOW.map((c, i) => <WorkflowCard key={i} c={c} i={i}/>)}
          </div>
          <div id="bmw-register" className="hidden lg:block absolute -bottom-28 -right-2 w-[320px] xl:w-[340px]">
            <RegisterForm/>
          </div>
        </div>
      </div>
      <div className="lg:hidden mt-8" id="bmw-register-mobile"><RegisterForm compact/></div>
    </section>
  );
}

function OpportunityCard({ o }) {
  return (
    <article data-testid={`bmw-opp-${o.key}`}
             className="group rounded-2xl bg-white border border-[#F0EEEA] shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-[box-shadow,transform] duration-300 overflow-hidden flex">
      <div className="flex-1 p-4 min-w-0 flex flex-col">
        <div className="flex items-start gap-3">
          <IconTile Icon={o.Icon} size={17} className="w-9 h-9"/>
          <h3 className="font-heading text-[15px] text-slate-900 leading-tight">{o.title}</h3>
        </div>
        <p className="text-[12px] text-slate-600 mt-3 leading-relaxed flex-1">{o.body}</p>
        <a href="#bmw-register" data-testid={`bmw-opp-${o.key}-cta`}
           className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#D23247]">
          Explore Partnership <ArrowRight size={11} weight="bold" className="group-hover:translate-x-0.5 transition-transform"/>
        </a>
      </div>
      <div className="relative w-[42%] shrink-0 bg-[#F7F6F2]">
        <img src={o.photo} alt={o.title} loading="lazy"
             className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
      </div>
    </article>
  );
}

const STATS = [
  { Icon: Biohazard,     n: 12, label: "New Requests", tone: "text-[#D23247]" },
  { Icon: CalendarBlank, n: 28, label: "Scheduled",    tone: "text-[#2563EB]" },
  { Icon: CheckCircle,   n: 24, label: "Completed",    tone: "text-[#15803D]" },
  { Icon: Truck,         n: 4,  label: "In Progress",  tone: "text-[#D97706]" },
];
const TODAY = [
  { name: "City Care Hospital", place: "Kazhakkoottam", status: "Collected",   kg: "8.6 kg", cls: "bg-[#DBEAFE] text-[#1D4ED8]" },
  { name: "LifeLine Clinic",    place: "Kowdiar",       status: "In Progress", kg: "5.2 kg", cls: "bg-[#DCFCE7] text-[#15803D]" },
  { name: "Sunrise Diagnostics",place: "Pattom",        status: "Scheduled",   kg: "—",      cls: "bg-[#E0E7FF] text-[#4338CA]" },
  { name: "Green Leaf Clinic",  place: "Vattiyoorkavu", status: "Pending",     kg: "—",      cls: "bg-[#FDE2E4] text-[#B52236]" },
];
const NAV = [SquaresFour, Biohazard, Path, UsersFour, ChartBar, Wallet, Files, GearSix];

function RouteMap() {
  return (
    <svg viewBox="0 0 220 120" className="w-full h-full" aria-hidden="true">
      <rect width="220" height="120" fill="#EEF2F6"/>
      <path d="M0 90 Q60 70 110 85 T220 70" stroke="#C7D7E8" strokeWidth="10" fill="none"/>
      <path d="M40 0 L70 120 M150 0 L130 120" stroke="#FFFFFF" strokeWidth="4"/>
      <path d="M30 40 L75 30 L95 62 L140 48 L180 28" stroke={RED} strokeWidth="2.5" fill="none" strokeDasharray="5 3"/>
      {[[30, 40], [75, 30], [140, 48], [180, 28]].map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="5" fill={RED} stroke="#fff" strokeWidth="2"/>
      ))}
      <circle cx="95" cy="62" r="5" fill="#2563EB" stroke="#fff" strokeWidth="2"/>
      <text x="100" y="80" fontSize="8" fill="#334155" fontWeight="600">Thiruvananthapuram</text>
    </svg>
  );
}

function DashboardMock() {
  return (
    <div data-testid="bmw-dashboard-mock"
         className="rounded-2xl overflow-hidden border border-[#E5E2D9] shadow-2xl bg-white flex text-slate-800">
      <aside className="hidden sm:flex flex-col gap-1 bg-[#0F172A] p-3 w-[120px] shrink-0">
        <div className="text-white font-heading text-sm mb-3 leading-none">rescigo<div className="text-[9px] text-[#F87171]">health</div></div>
        {NAV.map((I, i) => (
          <div key={i} className={`h-6 rounded-md flex items-center gap-1.5 px-2 ${i === 0 ? "bg-[#D23247] text-white" : "text-slate-400"}`}>
            <I size={11}/><span className="h-1.5 rounded bg-current opacity-50 flex-1"/>
          </div>
        ))}
      </aside>
      <div className="flex-1 p-4 min-w-0">
        <div className="flex items-center justify-between">
          <div className="font-heading text-sm">Collection Overview</div>
          <div className="text-[9px] text-slate-500 border border-slate-200 rounded px-1.5 py-0.5">This week</div>
        </div>
        <div className="grid grid-cols-4 gap-2 mt-3">
          {STATS.map(s => (
            <div key={s.label} className="rounded-lg border border-slate-100 p-2">
              <s.Icon size={14} weight="duotone" className={s.tone}/>
              <div className="font-heading text-base leading-none mt-1">{s.n}</div>
              <div className="text-[9px] text-slate-500 mt-0.5 truncate">{s.label}</div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <div className="rounded-lg border border-slate-100 p-2">
            <div className="text-[10px] font-semibold mb-1.5">Collection Routes</div>
            <div className="h-24 rounded overflow-hidden"><RouteMap/></div>
          </div>
          <div className="rounded-lg border border-slate-100 p-2">
            <div className="text-[10px] font-semibold mb-1.5">Today's Collections</div>
            <ul className="space-y-1.5">
              {TODAY.map(t => (
                <li key={t.name} className="flex items-center gap-1.5">
                  <Hospital size={11} className="text-[#D23247] shrink-0"/>
                  <div className="min-w-0 flex-1">
                    <div className="text-[9px] font-semibold truncate">{t.name}</div>
                    <div className="text-[8px] text-slate-400 truncate">{t.place}</div>
                  </div>
                  <span className={`text-[8px] px-1.5 py-0.5 rounded-full ${t.cls}`}>{t.status}</span>
                  <span className="text-[8px] text-slate-500 w-7 text-right">{t.kg}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

function Benefits() {
  return (
    <section id="bmw-benefits" className="pb-16 max-w-7xl mx-auto px-5 lg:px-10">
      <div className="grid lg:grid-cols-[1.2fr_1fr] gap-8 lg:gap-12 items-center">
        <div>
          <Eyebrow>Platform Benefits</Eyebrow>
          <h2 className="font-heading text-3xl sm:text-4xl text-slate-900 mt-3">
            More than collection. <span className="text-[#D23247]">A connected operations platform.</span>
          </h2>
          <p className="text-sm text-slate-600 mt-2">
            Digital tools, workflows and support to help your waste management business operate efficiently.
          </p>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {BENEFITS.map(b => (
              <div key={b.title} data-testid={`bmw-benefit-${b.title.replace(/[^a-z]+/gi, "-").toLowerCase()}`}
                   className="p-4 rounded-xl bg-white border border-[#F0EEEA] flex gap-3 hover:border-[#F5B5BD] transition-colors">
                <IconTile Icon={b.Icon} size={17} className="w-9 h-9"/>
                <div className="min-w-0">
                  <div className="font-heading text-[13px] text-slate-900 leading-tight">{b.title}</div>
                  <p className="text-[11px] text-slate-600 mt-1 leading-relaxed">{b.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <DashboardMock/>
      </div>
    </section>
  );
}

function Process() {
  return (
    <section className="bg-[#FDE2E4]/40">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-12 grid lg:grid-cols-[300px_1fr] gap-8 items-center">
        <div>
          <Eyebrow>How Partnership Works</Eyebrow>
          <h2 className="font-heading text-3xl text-slate-900 mt-3">A simple and transparent process.</h2>
          <p className="text-sm text-slate-600 mt-2">From registration to activation, we make it easy to get started.</p>
        </div>
        <ol className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4">
          {PROCESS.map((p, i) => (
            <li key={p.title} data-testid={`bmw-step-${i + 1}`}
                className="relative rounded-2xl bg-white border border-[#F0EEEA] p-4">
              <div className="text-[10px] font-mono text-[#D23247]">0{i + 1}</div>
              <IconTile Icon={p.Icon} size={17} className="w-9 h-9 mt-2"/>
              <div className="font-heading text-[13px] text-slate-900 mt-3 leading-tight">{p.title}</div>
              <p className="text-[11px] text-slate-600 mt-1 leading-snug">{p.body}</p>
              {i < PROCESS.length - 1 && (
                <ArrowRight size={14} weight="bold"
                            className="hidden md:block absolute -right-3 top-1/2 -translate-y-1/2 text-[#D23247]"/>
              )}
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section id="partner-contact" className="py-14 max-w-7xl mx-auto px-5 lg:px-10">
      <div className="rounded-3xl bg-[#0F172A] text-white p-6 sm:p-10 flex flex-wrap items-center gap-6">
        <div className="w-14 h-14 rounded-2xl bg-[#D23247] grid place-items-center shrink-0">
          <Handshake size={26} weight="duotone"/>
        </div>
        <div className="flex-1 min-w-[260px]">
          <h3 className="font-heading text-xl sm:text-2xl">Build a safer, cleaner collection network with us.</h3>
          <p className="text-sm text-slate-300 mt-1">
            Join authorised waste management operators serving hospitals, clinics and institutions across Kerala.
          </p>
        </div>
        <a href="#bmw-register" data-testid="bmw-final-apply"
           className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-[#D23247] hover:bg-[#B52236] text-sm font-semibold transition-colors">
          Apply as a Waste Partner <ArrowRight size={14} weight="bold"/>
        </a>
      </div>
    </section>
  );
}

export default function PartnerBmwLanding() {
  const images = usePageImages("partner-bmw");
  const jsonLd = [organizationSchema(), ...CITY_BRANCHES.map(localBusinessSchema)];
  const breadcrumbs = [
    { name: "Home", path: "/" },
    { name: "Partner with us", path: "/partner" },
    { name: "Waste Management Partners", path: "/partner-bmw" },
  ];
  return (
    <div className="min-h-screen bg-white" data-testid="page-partner-bmw">
      <style>{`@keyframes bmwRise{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:none}}
        .bmw-rise{animation:bmwRise .6s cubic-bezier(.2,.7,.2,1) both}`}</style>
      <SEO
        title="Waste Management Partners — grow your biomedical waste collection network | Rescigo Health"
        description="Register your biomedical waste management company with Rescigo Health. Get collection requests, route coordination, QR bag tracking and compliance documentation tools."
        path="/partner-bmw"
        keywords="biomedical waste partner Kerala, BMW collection partner, waste management partner, medical waste transport partner"
        jsonLd={jsonLd}
        breadcrumbs={breadcrumbs}
      />
      <MarketingNav/>
      <Hero img={images["hero.main"] || HERO_DEFAULT}/>

      <section id="bmw-opportunities" className="pt-6 pb-16 max-w-7xl mx-auto px-5 lg:px-10">
        <Eyebrow>Partner Opportunities</Eyebrow>
        <h2 className="font-heading text-3xl sm:text-4xl text-slate-900 mt-3">
          One platform. <span className="text-[#D23247]">Multiple waste management opportunities.</span>
        </h2>
        <p className="text-sm text-slate-600 mt-2">
          Explore service enquiries, contracts and operational coordination through Rescigo Health.
        </p>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {OPPORTUNITIES.map(o => (
            <OpportunityCard key={o.key} o={{ ...o, photo: images[`opportunities.${o.key}`] || o.photo }}/>
          ))}
        </div>
      </section>

      <Benefits/>
      <Process/>
      <FinalCta/>
      <MarketingFooter/>
    </div>
  );
}
